import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import ManageOrg from 'js/routes/Organizations/components/ManageOrg/ManageOrg';
import {
  addChildToOrg,
  addTeamsToOrg,
  createOrg,
  deleteOrg,
  removeChildFromOrg,
  removeTeamFromOrg,
  updateOrg,
  moveChildToOrg,
  moveTeamToOrg,
} from 'js/actions/org-actions';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api/schemas';
import { notify } from 'js/actions/alert-actions';
import { selectWorkspaceOrgsQuery } from 'js/store/reducers/queries';
import { selectActiveWorkspace } from 'js/store/reducers/workspaces';

const OrgStructureContainer = ({
  organization,
  loading,
  errorLoading,
  refreshOrg,
  teams,
  addTeamsToOrg,
  notify,
  createOrg,
  organizationsQueryResult,
  addChildToOrg,
  removeChildFromOrg,
  removeTeamFromOrg,
  deleteOrg,
  updateOrg,
  moveChildToOrg,
  moveTeamToOrg,
}) => {
  const handleAddTeamsToOrg = useCallback(
    (org, teamIds) => {
      return (async () => {
        try {
          await addTeamsToOrg(org, teamIds);
          await refreshOrg();
          notify({
            type: 'success',
            message: 'The teams have been added!',
          });
        } catch (e) {
          console.error('failed to add teams to org:', e);
          notify({
            type: 'danger',
            message:
              'Uh oh! There was a problem adding the teams to the organization.',
          });
        }
      })();
    },
    [addTeamsToOrg, notify, refreshOrg]
  );

  const handleAddGroup = useCallback(
    (parent, group) => {
      return (async () => {
        try {
          await createOrg(group, parent.id);
          await refreshOrg();
          notify({
            type: 'success',
            message: 'The group has been added!',
          });
        } catch (e) {
          console.error('failed to create the org:', e);
          notify({
            type: 'danger',
            message: 'Uh oh! There was a problem adding the group.',
          });
        }
      })();
    },
    [createOrg, notify, refreshOrg]
  );

  const handleAddOrgRef = useCallback(
    (parent, orgRefId) => {
      return (async () => {
        try {
          await addChildToOrg(parent.id, orgRefId);
          await refreshOrg();
          notify({
            type: 'success',
            message: 'The organization has been added!',
          });
        } catch (e) {
          console.error('failed to add the organization:', e);
          notify({
            type: 'danger',
            message: 'Uh oh! There was a problem adding the organization.',
          });
        }
      })();
    },
    [addChildToOrg, refreshOrg, notify]
  );

  const handleRemoveNode = useCallback(
    (parent, nodeToRemove) => {
      return (async () => {
        let term;
        try {
          if (nodeToRemove.type === 'team') {
            term = 'team';
            await removeTeamFromOrg(parent.id, nodeToRemove.id);
          } else {
            if (nodeToRemove.isRoot) {
              term = 'organization';
              await removeChildFromOrg(parent.id, nodeToRemove.id);
            } else {
              term = 'group';
              await deleteOrg(nodeToRemove);
            }
          }
          await refreshOrg();
          notify({
            type: 'success',
            message: `The ${term} has been removed!`,
          });
        } catch (e) {
          console.error(`failed to remove the ${term}:`, e);
          notify({
            type: 'danger',
            message: `Uh oh! There was a problem removing the ${term}.`,
          });
        }
      })();
    },
    [refreshOrg, notify, removeTeamFromOrg, removeChildFromOrg, deleteOrg]
  );

  const handleRename = useCallback(
    (pathToNode, node, newName) => {
      return (async () => {
        const term = node.isRoot ? 'organization' : 'group';
        let names;
        if (pathToNode[pathToNode.length - 1]) {
          names = pathToNode[pathToNode.length - 1].children
            .filter((o) => o.type === 'org')
            .map((o) => o.name.toUpperCase());
        } else {
          names = (organizationsQueryResult?.data ?? []).map((o) =>
            o.name.toUpperCase()
          );
        }

        if (names.includes(newName.toUpperCase())) {
          notify({
            type: 'danger',
            message: 'Oops! That name is already taken.',
          });
          return;
        }

        try {
          await updateOrg(node.id, { name: newName });
          await refreshOrg();
        } catch (e) {
          console.error(`failed to rename the ${term}`, e);
          notify({
            type: 'danger',
            message: `Uh oh! There was a problem renaming the ${term}.`,
          });
        }
      })();
    },
    [updateOrg, refreshOrg, notify, organizationsQueryResult]
  );

  const handlePromoteToOrg = useCallback(
    (node) => {
      return (async () => {
        try {
          await updateOrg(node.id, { is_root: true });
          await refreshOrg();
        } catch (e) {
          console.error(`failed to promote the group to an organization`, e);
          notify({
            type: 'danger',
            message: `Uh oh! There was a problem promoting the group.`,
          });
        }
      })();
    },
    [updateOrg, refreshOrg, notify]
  );

  const handleReparentGroup = useCallback(
    ({ childId, fromParentId, toParentId }) => {
      return (async () => {
        try {
          await moveChildToOrg(fromParentId, childId, toParentId);
          await refreshOrg();
          notify({
            type: 'success',
            message: 'The group has been moved!',
          });
        } catch (e) {
          console.error('failed to move the group:', e);
          notify({
            type: 'danger',
            message: 'Uh oh! There was a problem moving the group.',
          });
          throw e;
        }
      })();
    },
    [moveChildToOrg, refreshOrg, notify]
  );

  const handleMoveTeam = useCallback(
    ({ teamId, fromOrgId, toOrgId, node, targetNode }) => {
      return (async () => {
        try {
          const response = await moveTeamToOrg(fromOrgId, teamId, toOrgId);
          await refreshOrg();

          if (response?.result === 'already_on_target') {
            notify({
              type: 'info',
              message: `${node.name} is already under ${targetNode.name} — it was removed from its previous location.`,
            });
          } else {
            notify({
              type: 'success',
              message: 'The team has been moved!',
            });
          }
        } catch (e) {
          console.error('failed to move the team:', e);
          notify({
            type: 'danger',
            message: 'Uh oh! There was a problem moving the team.',
          });
          throw e;
        }
      })();
    },
    [moveTeamToOrg, refreshOrg, notify]
  );

  return (
    <ManageOrg
      organization={organization}
      errorLoading={errorLoading}
      loading={loading}
      teams={teams}
      rootOrgs={organizationsQueryResult?.data ?? []}
      onAddTeamsToOrg={handleAddTeamsToOrg}
      onAddGroup={handleAddGroup}
      onAddOrgRef={handleAddOrgRef}
      onRemoveNode={handleRemoveNode}
      onRename={handleRename}
      onPromoteToOrg={handlePromoteToOrg}
      onReparentGroup={handleReparentGroup}
      onMoveTeam={handleMoveTeam}
    />
  );
};

export default connect(
  (store) => {
    const workspace = selectActiveWorkspace(store);
    const workspaceId = workspace?.id;
    const teamsQueryId = `all-teams-${workspaceId}`;
    return {
      teams: denormalize(
        store.queries[teamsQueryId]?.result ?? [],
        SCHEMAS.TEAM_ARRAY,
        store.entities
      ),
      organizationsQueryResult: workspaceId
        ? selectWorkspaceOrgsQuery(store, workspaceId)
        : null,
    };
  },
  {
    addTeamsToOrg,
    createOrg,
    notify,
    addChildToOrg,
    removeChildFromOrg,
    removeTeamFromOrg,
    deleteOrg,
    updateOrg,
    moveChildToOrg,
    moveTeamToOrg,
  }
)(OrgStructureContainer);
